/**
 * Simple in-memory cache for API responses
 */
interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface CacheOptions {
  /** Time to live in milliseconds */
  ttl?: number;
  /** Cache key prefix */
  prefix?: string;
}

class ApiCache {
  private cache: Map<string, CacheItem<any>> = new Map();
  private defaultTTL: number = 5 * 60 * 1000; // 5 minutes
  private prefix: string = "api-cache:";

  /**
   * Set cache options
   * @param options - Cache options
   */
  setOptions(options: CacheOptions): void {
    if (options.ttl) this.defaultTTL = options.ttl;
    if (options.prefix) this.prefix = options.prefix;
  }

  /**
   * Generate a cache key from the function name and arguments
   * @param fnName - Function name
   * @param args - Function arguments
   * @returns Cache key
   */
  private generateKey(fnName: string, args: any[]): string {
    const argsString = JSON.stringify(args);
    return `${this.prefix}${fnName}:${argsString}`;
  }

  /**
   * Get data from cache
   * @param key - Cache key
   * @returns Cached data or null if not found or expired
   */
  private get<T>(key: string): T | null {
    const item = this.cache.get(key);

    if (!item) return null;

    // Check if the item has expired
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  /**
   * Set data in cache
   * @param key - Cache key
   * @param data - Data to cache
   * @param ttl - Time to live in milliseconds
   */
  private set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    const now = Date.now();

    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    });
  }

  /**
   * Clear all cache or specific key
   * @param key - Optional specific key to clear
   */
  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Clear all expired cache items
   */
  clearExpired(): void {
    const now = Date.now();

    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiresAt) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Wrap an API function with caching
   * @param fn - API function to wrap
   * @param ttl - Optional custom TTL for this function
   * @returns Wrapped function with caching
   */
  withCache<T, Args extends any[]>(
    fn: (...args: Args) => Promise<T>,
    ttl?: number
  ): (...args: Args) => Promise<T> {
    return async (...args: Args): Promise<T> => {
      const key = this.generateKey(fn.name || "anonymous", args);
      const cachedData = this.get<T>(key);

      if (cachedData !== null) {
        return cachedData;
      }

      const data = await fn(...args);
      this.set(key, data, ttl);

      return data;
    };
  }
}

// Export a singleton instance
export const apiCache = new ApiCache();

/**
 * Decorator function to cache API calls
 * @param ttl - Optional custom TTL for this function
 * @returns Decorated function with caching
 */
export function withCache<T, Args extends any[]>(
  ttl?: number
): (
  target: any,
  propertyKey: string,
  descriptor: TypedPropertyDescriptor<(...args: Args) => Promise<T>>
) => TypedPropertyDescriptor<(...args: Args) => Promise<T>> {
  return (
    target: any,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<(...args: Args) => Promise<T>>
  ): TypedPropertyDescriptor<(...args: Args) => Promise<T>> => {
    const originalMethod = descriptor.value;

    if (originalMethod) {
      descriptor.value = apiCache.withCache(originalMethod, ttl);
    }

    return descriptor;
  };
}
